import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { api, newKey } from '../api/client';
import { useInvalidateStay, useRooms } from '../api/queries';
import type { Booking, FolioEntry } from '../api/types';
import { DEPARTMENTS, DEPARTMENT_LABEL, formatGhs, parseCedis } from '../format';
import { Button, Field, Modal, useReport, useToast } from '../ui/kit';

interface Base {
  open: boolean;
  onClose: () => void;
}

/** A retry of the same submission must reuse its key; a new submission gets a new one. */
function useKey() {
  const k = useRef(newKey());
  return { get: () => k.current, renew: () => (k.current = newKey()) };
}

/* ------------------------------------------------------------------- charge */
export function ChargeDialog({ open, onClose, bookingId }: Base & { bookingId: string }) {
  const [department, setDepartment] = useState<string>('FOOD');
  const [description, setDescription] = useState('');
  const [amount, setAmount] = useState('');
  const key = useKey();
  const inv = useInvalidateStay();
  const toast = useToast();
  const report = useReport();
  const pesewas = parseCedis(amount);

  const m = useMutation({
    mutationFn: () => api(`/bookings/${bookingId}/charges`, { method: 'POST', idempotencyKey: key.get(), body: { department, description: description.trim(), amount: pesewas } }),
    onSuccess: () => {
      toast.ok('Charge added');
      key.renew();
      setDescription('');
      setAmount('');
      inv(bookingId);
      onClose();
    },
    onError: report,
  });

  const valid = pesewas !== null && pesewas > 0 && description.trim().length >= 2;
  return (
    <Modal open={open} onClose={onClose} title="Add a charge">
      <form
        className="stack"
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          if (valid) m.mutate();
        }}
      >
        <Field id="dept" label="Department">
          <select id="dept" className="select" value={department} onChange={(e) => setDepartment(e.target.value)}>
            {DEPARTMENTS.map((d) => (
              <option key={d} value={d}>
                {DEPARTMENT_LABEL[d]}
              </option>
            ))}
          </select>
        </Field>
        <Field id="desc" label="What is it for?">
          <input id="desc" className="input" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="e.g. Dinner for two" autoFocus />
        </Field>
        <Field id="amt" label="Amount (GH₵)" hint="The price the guest pays, tax included." error={amount && pesewas === null ? 'Enter an amount like 85 or 85.50' : undefined}>
          <input id="amt" className="input" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0.00" />
        </Field>
        <div className="modal-foot">
          <Button type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={!valid} loading={m.isPending}>
            Add charge
          </Button>
        </div>
      </form>
    </Modal>
  );
}

/* ------------------------------------------------------------------ payment */
type PayTab = 'CASH' | 'BANK_TRANSFER' | 'MOMO';

export function PaymentDialog({ open, onClose, bookingId, balance }: Base & { bookingId: string; balance: number }) {
  const [tab, setTab] = useState<PayTab>('CASH');
  const [amount, setAmount] = useState('');
  const [phone, setPhone] = useState('');
  const [network, setNetwork] = useState('MTN');
  const [note, setNote] = useState('');
  const [momoId, setMomoId] = useState<string | null>(null);
  const key = useKey();
  const inv = useInvalidateStay();
  const toast = useToast();
  const report = useReport();

  // Reset only when the dialog opens. The balance changes under us when a payment lands, and that must not wipe the result screen.
  const balanceRef = useRef(balance);
  balanceRef.current = balance;
  useEffect(() => {
    if (open) {
      setAmount(balanceRef.current > 0 ? (balanceRef.current / 100).toFixed(2) : '');
      setMomoId(null);
    }
  }, [open]);

  const pesewas = parseCedis(amount);
  const valid = pesewas !== null && pesewas > 0 && (tab !== 'MOMO' || /^\+?[0-9]{9,15}$/.test(phone.trim()));

  const offline = useMutation({
    mutationFn: () => api(`/bookings/${bookingId}/payments/offline`, { method: 'POST', idempotencyKey: key.get(), body: { method: tab, amount: pesewas, ...(note.trim() ? { note: note.trim() } : {}) } }),
    onSuccess: () => {
      toast.ok('Payment recorded');
      key.renew();
      inv(bookingId);
      onClose();
    },
    onError: report,
  });

  const momo = useMutation({
    mutationFn: () => api<{ payment: { id: string } }>(`/bookings/${bookingId}/payments/momo`, { method: 'POST', idempotencyKey: key.get(), body: { amount: pesewas, phone: phone.trim(), network } }),
    onSuccess: (r) => {
      key.renew();
      setMomoId(r.payment.id);
    },
    onError: report,
  });

  // After a MoMo request the guest approves on their phone; the server learns the result from the provider.
  const status = useQuery({
    queryKey: ['payment', momoId],
    enabled: !!momoId,
    queryFn: () => api<{ status: string }>(`/payments/${momoId}`),
    refetchInterval: (q) => (q.state.data && q.state.data.status !== 'PENDING' ? false : 3000),
    refetchIntervalInBackground: true, // the guest is approving on their phone; keep checking even if this tab is not in front
  });
  const final = status.data?.status;
  useEffect(() => {
    if (final === 'SUCCEEDED') {
      toast.ok('Mobile money payment received');
      inv(bookingId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [final]);

  if (momoId) {
    return (
      <Modal open={open} onClose={onClose} title="Mobile money payment">
        <div className="stack" role="status">
          {final === 'SUCCEEDED' ? (
            <div className="banner good">Payment received. Thank you.</div>
          ) : final === 'FAILED' || final === 'EXPIRED' ? (
            <div className="banner bad">{final === 'FAILED' ? 'The payment was declined or cancelled.' : 'The request timed out before the guest approved it.'}</div>
          ) : (
            <div className="banner info">Waiting for the guest to approve the prompt on their phone…</div>
          )}
          <p className="small muted">The balance updates by itself once the provider confirms. You can close this and carry on.</p>
          <div className="modal-foot">
            <Button variant="primary" onClick={onClose}>
              Done
            </Button>
          </div>
        </div>
      </Modal>
    );
  }

  return (
    <Modal open={open} onClose={onClose} title="Take a payment">
      <form
        className="stack"
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          if (!valid) return;
          if (tab === 'MOMO') momo.mutate();
          else offline.mutate();
        }}
      >
        <div className="seg" role="group" aria-label="Payment method">
          {(
            [
              ['CASH', 'Cash'],
              ['MOMO', 'Mobile money'],
              ['BANK_TRANSFER', 'Bank transfer'],
            ] as const
          ).map(([id, label]) => (
            <button key={id} type="button" aria-pressed={tab === id} onClick={() => setTab(id)}>
              {label}
            </button>
          ))}
        </div>
        <Field id="pamt" label="Amount (GH₵)" hint={balance > 0 ? `Balance due ${formatGhs(balance)}` : undefined} error={amount && pesewas === null ? 'Enter an amount like 150 or 150.50' : undefined}>
          <input id="pamt" className="input" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} autoFocus />
        </Field>
        {tab === 'MOMO' ? (
          <>
            <Field id="net" label="Network">
              <select id="net" className="select" value={network} onChange={(e) => setNetwork(e.target.value)}>
                <option value="MTN">MTN</option>
                <option value="TELECEL">Telecel</option>
                <option value="AIRTELTIGO">AirtelTigo</option>
              </select>
            </Field>
            <Field id="mphone" label="Guest's MoMo number" hint="Digits only, e.g. 0241234567">
              <input id="mphone" className="input" inputMode="tel" value={phone} onChange={(e) => setPhone(e.target.value)} />
            </Field>
          </>
        ) : (
          <Field id="note" label="Note (optional)">
            <input id="note" className="input" value={note} onChange={(e) => setNote(e.target.value)} placeholder={tab === 'CASH' ? 'e.g. receipt no.' : 'e.g. transfer reference'} />
          </Field>
        )}
        <div className="modal-foot">
          <Button type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={!valid} loading={offline.isPending || momo.isPending}>
            {tab === 'MOMO' ? 'Send request' : 'Record payment'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

/* --------------------------------------------------------------------- void */
export function VoidDialog({ open, onClose, bookingId, entry }: Base & { bookingId: string; entry: FolioEntry | null }) {
  const [reason, setReason] = useState('');
  const key = useKey();
  const inv = useInvalidateStay();
  const toast = useToast();
  const report = useReport();

  const m = useMutation({
    mutationFn: () => api(`/bookings/${bookingId}/folio/entries/${entry!.id}/void`, { method: 'POST', idempotencyKey: key.get(), body: { reason: reason.trim() } }),
    onSuccess: () => {
      toast.ok('Charge voided');
      key.renew();
      setReason('');
      inv(bookingId);
      onClose();
    },
    onError: report,
  });

  return (
    <Modal open={open && !!entry} onClose={onClose} title="Void this charge?">
      <form
        className="stack"
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          if (reason.trim().length >= 3) m.mutate();
        }}
      >
        {entry ? (
          <p>
            <strong>{entry.description}</strong> · {formatGhs(entry.amount)}
          </p>
        ) : null}
        <p className="small muted">The charge stays on the folio, struck through, with your reason. It is recorded in the audit log.</p>
        <Field id="vr" label="Reason">
          <input id="vr" className="input" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Billed to the wrong room" autoFocus />
        </Field>
        <div className="modal-foot">
          <Button type="button" onClick={onClose}>
            Keep it
          </Button>
          <Button variant="danger" type="submit" disabled={reason.trim().length < 3} loading={m.isPending}>
            Void charge
          </Button>
        </div>
      </form>
    </Modal>
  );
}

/* ------------------------------------------------------------ reason prompt */
export function ReasonDialog({ open, onClose, title, label, confirmLabel, onSubmit, pending }: Base & { title: string; label: string; confirmLabel: string; onSubmit: (reason: string) => void; pending: boolean }) {
  const [reason, setReason] = useState('');
  return (
    <Modal open={open} onClose={onClose} title={title}>
      <form
        className="stack"
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          if (reason.trim().length >= 3) onSubmit(reason.trim());
        }}
      >
        <Field id="rs" label={label}>
          <input id="rs" className="input" value={reason} onChange={(e) => setReason(e.target.value)} autoFocus />
        </Field>
        <div className="modal-foot">
          <Button type="button" onClick={onClose}>
            Back
          </Button>
          <Button variant="danger" type="submit" disabled={reason.trim().length < 3} loading={pending}>
            {confirmLabel}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

/* -------------------------------------------------------------- assign room */
export function AssignRoomDialog({ open, onClose, booking, onAssign, pending }: Base & { booking: Booking; onAssign: (roomId?: string) => void; pending: boolean }) {
  const rooms = useRooms();
  const [pick, setPick] = useState('');
  const mine = (rooms.data ?? []).filter((r) => r.roomType.id === booking.roomType.id && r.status !== 'OUT_OF_ORDER');
  return (
    <Modal open={open} onClose={onClose} title="Assign a room">
      <div className="stack">
        <p className="small muted">The system picks a room that is free for every night of the stay, or you can choose one. Rooms that are not clean can still be assigned; they must be ready by check-in.</p>
        <Button variant="primary" block loading={pending && !pick} onClick={() => onAssign()}>
          Pick one for me
        </Button>
        <Field id="rm" label="Or choose a room">
          <select id="rm" className="select" value={pick} onChange={(e) => setPick(e.target.value)}>
            <option value="">Select…</option>
            {mine.map((r) => (
              <option key={r.id} value={r.id}>
                Room {r.number}
              </option>
            ))}
          </select>
        </Field>
        <div className="modal-foot">
          <Button type="button" onClick={onClose}>
            Close
          </Button>
          <Button disabled={!pick} loading={pending && !!pick} onClick={() => onAssign(pick)}>
            Assign room {mine.find((r) => r.id === pick)?.number ?? ''}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

/* ---------------------------------------------------------------- check-out */
export function CheckOutDialog({ open, onClose, balance, canOverride, onConfirm, pending }: Base & { balance: number; canOverride: boolean; onConfirm: (override: boolean) => void; pending: boolean }) {
  const [override, setOverride] = useState(false);
  const owing = balance > 0;
  return (
    <Modal open={open} onClose={onClose} title="Check out">
      <div className="stack">
        {owing ? (
          <div className="banner">The folio still shows {formatGhs(balance)} owing. Take payment first, or a manager can approve leaving the balance.</div>
        ) : balance < 0 ? (
          <div className="banner info">The guest has paid {formatGhs(-balance)} more than the folio total. Refund or correct it before checking out.</div>
        ) : (
          <div className="banner good">The folio is settled.</div>
        )}
        {balance !== 0 && canOverride ? (
          <label className="row" style={{ cursor: 'pointer' }}>
            <input type="checkbox" checked={override} onChange={(e) => setOverride(e.target.checked)} style={{ width: 22, height: 22 }} />
            <span>Manager approval: check out with this balance</span>
          </label>
        ) : null}
        <div className="modal-foot">
          <Button type="button" onClick={onClose}>
            Back
          </Button>
          <Button variant="primary" disabled={balance !== 0 && !override} loading={pending} onClick={() => onConfirm(override)}>
            Check out
          </Button>
        </div>
      </div>
    </Modal>
  );
}
